import type { ReactNode } from "react";
import { eyebrowClass, themeClasses, type Theme } from "@cw/design-tokens/theme";
import { Container } from "../primitives/Container";
import { LinkRow, type LinkSpec } from "../primitives/Button";

export interface HeroProps {
  eyebrow?: string;
  heading: string;
  subheading?: string;
  links?: LinkSpec[];
  actions?: ReactNode;
  theme?: Theme;
  align?: "left" | "center";
  /** Rendered above the heading (centered) or beside it (left). */
  visual?: ReactNode;
  /** True for the page's opening hero (h1). */
  first?: boolean;
  compact?: boolean;
}

export function Hero({ eyebrow, heading, subheading, links, actions, theme = "paper", align = "left", visual, first = true, compact = false }: HeroProps) {
  const t = themeClasses(theme);
  const centered = align === "center";
  const Tag = first ? "h1" : "h2";
  return (
    <section className={`relative isolate overflow-hidden ${t.surface} ${compact ? "pt-12 pb-12 md:pt-16 md:pb-16" : "pt-14 pb-16 md:pt-20 md:pb-24"}`}>
      <Container className="w-full">
        <div className={visual && !centered ? "grid items-center gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] md:gap-16" : ""}>
          {visual && centered ? <div className="cw-fade-up mx-auto mb-8 w-full max-w-6xl md:mb-12">{visual}</div> : null}
          <div className={centered ? "mx-auto max-w-3xl text-center" : "max-w-4xl"}>
            {eyebrow ? (
              <p className={`${eyebrowClass(theme)} cw-fade-up mb-5 flex items-center gap-3 font-sans text-xs font-semibold uppercase tracking-[0.2em] ${centered ? "justify-center" : ""}`}>
                <span aria-hidden="true" className="h-0.5 w-10 shrink-0 rounded-pill bg-signal" />{eyebrow}
              </p>
            ) : null}
            <Tag className={`${t.heading} cw-fade-up cw-delay-1 ${first ? "text-display-xl" : "text-display-lg"} font-semibold`}>{heading}</Tag>
            {subheading ? <p className={`${t.body} cw-fade-up cw-delay-2 mt-6 max-w-2xl text-lg leading-relaxed md:text-xl ${centered ? "mx-auto" : ""}`}>{subheading}</p> : null}
            {actions ? <div className={`cw-fade-up cw-delay-3 mt-9 flex flex-wrap gap-3 ${centered ? "justify-center" : ""}`}>{actions}</div> : <LinkRow links={links} theme={theme} size="lg" className={`cw-fade-up cw-delay-3 mt-9 ${centered ? "justify-center" : ""}`} />}
          </div>
          {visual && !centered ? <div className="cw-fade-up cw-delay-2 mt-10 md:mt-0">{visual}</div> : null}
        </div>
      </Container>
    </section>
  );
}
